import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { Button } from "components/ds";
import { format, isValid } from "date-fns";
import { CalendarClock, CheckCircle, Clock, X } from "lucide-react";
import {
  MF_ACCESS_REVIEWS,
  type MFDeadlineExtension,
} from "state/members/accessReviews";
import {
  deadlineExtensionAllowanceText,
  extensionAutoGrants,
  validateExtensionRequest,
} from "../reviewLogic";

/** Format an ISO date for the success copy, falling back to a dash. */
const formatDueDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  const parsed = new Date(value);
  return isValid(parsed) ? format(parsed, "d MMM yyyy") : "-";
};

export interface RequestExtensionModalProps {
  isOpen: boolean;
  onClose: () => void;
  accountSetId: string;
  deadlineExtension: MFDeadlineExtension;
  /** The review takes no more changes (its cycle ended): confirm is off. */
  isLocked?: boolean;
}

/**
 * RequestExtensionModal — the reviewer's "Request more time" form (PLO-4856).
 *
 * Thin over reviewLogic: the allowance copy, the per-field validation and the
 * instant-grant hint all come from pure helpers. On submit it POSTs to the
 * request-extension endpoint; an `auto_granted` response flips to a success
 * state (the review-details refetch — triggered by the mutation's query
 * invalidation — updates the displayed due date), while a `pending` response
 * confirms the request is queued for an admin.
 */
const RequestExtensionModal: React.FC<RequestExtensionModalProps> = ({
  isOpen,
  onClose,
  accountSetId,
  deadlineExtension,
  isLocked = false,
}) => {
  const toast = useToast();
  const { mutate, isLoading } = MF_ACCESS_REVIEWS.useRequestDeadlineExtension();

  const [days, setDays] = useState<number>(1);
  const [reason, setReason] = useState<string>("");
  // Outcome of a completed request: the success panel the modal flips to.
  // Deliberately does NOT carry a due date — the extension response never
  // includes the new date, and the pre-request `deadlineExtension` prop would
  // be stale at the instant the request resolves (invalidateQueries only
  // *starts* the review-details refetch; it doesn't await it). The success
  // panel instead reads the date straight off the live `deadlineExtension`
  // prop below, so it picks up the authoritative value the moment the
  // refetch lands and the parent re-renders this still-open modal.
  const [outcome, setOutcome] = useState<
    { kind: "auto_granted" } | { kind: "pending"; requestedDays: number } | null
  >(null);

  // Reset the form whenever the modal (re)opens.
  useEffect(() => {
    if (isOpen) {
      setDays(1);
      setReason("");
      setOutcome(null);
    }
  }, [isOpen]);

  const allowanceText = deadlineExtensionAllowanceText(deadlineExtension);
  const validation = useMemo(
    () => validateExtensionRequest(days, reason, deadlineExtension),
    [days, reason, deadlineExtension],
  );
  const willAutoGrant = extensionAutoGrants(days, deadlineExtension);

  const handleSubmit = async () => {
    if (!validation.valid || isLocked) return;

    try {
      const res = await mutate({
        accountSetId,
        requestedDays: days,
        reason: deadlineExtension.requireReason
          ? reason.trim()
          : reason.trim() || undefined,
      });
      // `mutate` (mutateAsync) resolves only on a success envelope; narrow on
      // the `success` discriminant so TS can see `data`.
      const data = res && res.success ? res.data : null;
      if (data?.status === "auto_granted") {
        setOutcome({ kind: "auto_granted" });
      } else {
        setOutcome({ kind: "pending", requestedDays: days });
      }
    } catch (err) {
      // useMemberMutate already surfaces an error toast; add a scoped one so the
      // reviewer sees the failure even if the global toast is missed.
      toast({
        title: "Couldn't request an extension",
        description:
          typeof err === "string"
            ? err
            : ((err as any)?.error ??
              "Something went wrong. Please try again."),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <ModalOverlay bg="rgba(0, 0, 0, 0.3)" backdropFilter="blur(3px)" />
      <ModalContent borderRadius="12px" boxShadow="lg">
        <ModalHeader
          fontSize="md"
          fontWeight="500"
          color="gray.800"
          pb={2}
          borderBottom="1px solid"
          borderColor="gray.100"
        >
          <HStack>
            <Icon as={CalendarClock} boxSize="16px" color="gray.500" />
            <Box>Request more time</Box>
          </HStack>
        </ModalHeader>
        <ModalCloseButton size="sm" top="12px" right="12px" />

        {outcome ? (
          /* --- Success state --------------------------------------------- */
          <>
            <ModalBody py={5} px={4}>
              <VStack spacing={3} align="flex-start">
                <Icon
                  as={outcome.kind === "auto_granted" ? CheckCircle : Clock}
                  boxSize="28px"
                  color={
                    outcome.kind === "auto_granted" ? "green.500" : "orange.500"
                  }
                />
                {outcome.kind === "auto_granted" ? (
                  <>
                    <Text fontSize="md" fontWeight="600" color="gray.800">
                      Extension granted
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Your deadline is now{" "}
                      <Text as="span" fontWeight="600" color="gray.800">
                        {formatDueDate(
                          deadlineExtension.effectiveDueDate ??
                            deadlineExtension.dueDateOverride,
                        )}
                      </Text>
                      .
                    </Text>
                  </>
                ) : (
                  <>
                    <Text fontSize="md" fontWeight="600" color="gray.800">
                      Request submitted
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      Your request for {outcome.requestedDays}{" "}
                      {outcome.requestedDays === 1 ? "day" : "days"} has been
                      sent for approval. You'll be notified once an admin
                      responds.
                    </Text>
                  </>
                )}
              </VStack>
            </ModalBody>
            <Divider borderColor="gray.100" />
            <ModalFooter pt={3} pb={3}>
              <Button
                variant="solid"
                tone="primary"
                size="md"
                onClick={onClose}
              >
                Done
              </Button>
            </ModalFooter>
          </>
        ) : (
          /* --- Request form ---------------------------------------------- */
          <>
            <ModalBody py={4} px={4}>
              <VStack spacing={4} align="stretch">
                <FormControl
                  isRequired
                  isInvalid={validation.daysError != null}
                >
                  <FormLabel fontSize="sm" fontWeight="500" mb={1}>
                    Additional days
                  </FormLabel>
                  <NumberInput
                    min={1}
                    value={days}
                    onChange={(_, valueAsNumber) =>
                      setDays(Number.isNaN(valueAsNumber) ? 0 : valueAsNumber)
                    }
                    size="sm"
                  >
                    <NumberInputField
                      placeholder="Number of days"
                      borderRadius="8px"
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  {validation.daysError ? (
                    <FormErrorMessage fontSize="xs">
                      {validation.daysError}
                    </FormErrorMessage>
                  ) : (
                    allowanceText && (
                      <FormHelperText fontSize="xs" mt={1}>
                        {allowanceText}
                      </FormHelperText>
                    )
                  )}
                  {willAutoGrant && !validation.daysError && (
                    <Text fontSize="xs" color="green.600" mt={1}>
                      This request will be granted instantly.
                    </Text>
                  )}
                </FormControl>

                <FormControl
                  isRequired={deadlineExtension.requireReason}
                  isInvalid={validation.reasonError != null}
                >
                  <FormLabel fontSize="sm" fontWeight="500" mb={1}>
                    Reason
                    {!deadlineExtension.requireReason && (
                      <Text as="span" color="gray.400" fontWeight="400">
                        {" "}
                        (optional)
                      </Text>
                    )}
                  </FormLabel>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Why do you need more time?"
                    fontSize="sm"
                    borderRadius="8px"
                    rows={3}
                    resize="vertical"
                  />
                  {validation.reasonError && (
                    <FormErrorMessage fontSize="xs">
                      {validation.reasonError}
                    </FormErrorMessage>
                  )}
                </FormControl>
              </VStack>
            </ModalBody>

            <Divider borderColor="gray.100" />

            <ModalFooter pt={3} pb={3}>
              <HStack spacing={2}>
                <Button
                  variant="subtle"
                  tone="neutral"
                  size="md"
                  onClick={onClose}
                  isDisabled={isLoading}
                  leadingIcon={<Icon as={X} boxSize="14px" />}
                >
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  tone="primary"
                  size="md"
                  onClick={handleSubmit}
                  isLoading={isLoading}
                  isDisabled={isLoading || !validation.valid || isLocked}
                >
                  Request extension
                </Button>
              </HStack>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default RequestExtensionModal;
